import { createAsyncThunk } from '@reduxjs/toolkit';
import { listCategories } from './marketplace-categories';
import { listSuppliers } from './marketplace-suppliers';
import MarketplaceProductsResource from 'api/service/MarketplaceProductsResource';

export const listProducts = createAsyncThunk(
  'marketplaceManagement/listProducts',
  async (initialData, { rejectWithValue }) => {
    const marketplaceProductsResource = new MarketplaceProductsResource();
    const { offset = 0, query = '' } = initialData || {};
    const response = await marketplaceProductsResource.list({
      size: 10,
      includes: 'supplier,category,variant_stats,shared_count',
      search: query || '',
      offset,
    });

    if (response?.status === 200) {
      return { data: response?.data || [], offset: 10 + offset };
    }
    return rejectWithValue('Products not found');
  }
);

export const listProductsNext = createAsyncThunk(
  'marketplaceManagement/listProductsNext',
  async (initialData, { rejectWithValue }) => {
    const marketplaceProductsResource = new MarketplaceProductsResource();
    const { offset = 0, query = '' } = initialData || {};
    const response = await marketplaceProductsResource.list({
      size: 10,
      includes: 'supplier,category,variant_stats,shared_count',
      search: query || '',
      offset,
    });

    if (response?.status === 200) {
      return { data: response?.data || [], offset: 10 + offset };
    }
    return rejectWithValue('Products not found');
  }
);

export const createProduct = createAsyncThunk(
  'marketplaceManagement/createProduct',
  async (initialData, { rejectWithValue, dispatch }) => {
    const marketplaceProductsResource = new MarketplaceProductsResource();
    const { product } = initialData;
    const productData = {
      name: product?.name,
      description: product?.description,
      supplier_id: product?.supplier,
      category_id: product?.category,
    };
    const { status, data } = await marketplaceProductsResource.create(productData);
    if (status === 201) {
      const productId = data?.id;
      const variants = product?.variants || [];
      const variantData = variants.map((variant) => ({
        name: variant?.name,
        description: variant?.name,
        price: variant?.price,
        annotation: {},
      }));
      await Promise.all(
        variantData.map((variant) => marketplaceProductsResource.product(productId).createVariant(variant))
      );
      await Promise.all([dispatch(listProducts()), dispatch(listCategories()), dispatch(listSuppliers())]);
      return data;
    }
    return rejectWithValue('Product creation failed.');
  }
);

export const updateProduct = createAsyncThunk(
  'marketplaceManagement/updateProduct',
  async (initialData, { rejectWithValue, dispatch }) => {
    const marketplaceProductsResource = new MarketplaceProductsResource();
    const { productId, product, newVariants, variantsToBeUpdated, variantsToBeDeleted, existingVariants } = initialData;

    // save new variants
    const createdVariantsResponses = await Promise.all(
      newVariants.map((variant) => marketplaceProductsResource.product(productId).createVariant(variant))
    );
    const successfulCreatedVariants = createdVariantsResponses
      .filter((res) => res?.status === 201)
      .map((res) => res?.data);

    // update existing variants
    await Promise.all(
      variantsToBeUpdated.map((variant) =>
        marketplaceProductsResource.variant(variant.id).update({ name: variant.name, price: variant.price })
      )
    );

    // delete existing variants
    await Promise.all(variantsToBeDeleted.map((variant) => marketplaceProductsResource.variant(variant.id).delete()));

    // update product
    const productData = {
      ...product,
      variant_sorting: [
        ...existingVariants.map((variant) => variant.id),
        ...successfulCreatedVariants.map((variant) => variant.id),
      ],
    };
    const { status, data } = await marketplaceProductsResource
      .product(productId)
      .update(productData, { includes: 'supplier,category,variant_stats,shared_count' });
    if (status === 200) {
      await Promise.all([dispatch(listCategories()), dispatch(listSuppliers())]);
      return data;
    }

    return rejectWithValue('Product updating failed.');
  }
);

export const deleteProduct = createAsyncThunk(
  'marketplaceManagement/deleteProduct',
  async (initialData, { rejectWithValue, dispatch }) => {
    const marketplaceProductsResource = new MarketplaceProductsResource();
    const { id } = initialData;
    const { status } = await marketplaceProductsResource.product(id).delete();
    if (status === 202) {
      await Promise.all([dispatch(listProducts()), dispatch(listCategories()), dispatch(listSuppliers())]);
      return id;
    }
    return rejectWithValue('Product deletion failed.');
  }
);

export const getProductVariants = createAsyncThunk(
  'marketplaceManagement/getProductVariants',
  async (initialData, { rejectWithValue }) => {
    const marketplaceProductsResource = new MarketplaceProductsResource();
    const id = initialData;
    const { status, data } = await marketplaceProductsResource.product(id).listVariants();
    if (status === 200) {
      return data;
    }
    return rejectWithValue('Product variants not found.');
  }
);
